// Shared bounded snapshots; invalidation also protects consumers of older reads.
export function createWorkspaceCache(load: (path: string) => Promise<unknown>) {
  const snapshots = new Map<string, { at: number; data: unknown }>();
  const reads = new Map<
    string,
    { valid: boolean; promise: Promise<unknown> }
  >();
  function snapshot<T>(path: string): T | undefined {
    const cached = snapshots.get(path);
    if (!cached || Date.now() - cached.at > 30_000) {
      snapshots.delete(path);
      return undefined;
    }
    return structuredClone(cached.data) as T;
  }
  async function read<T>(path: string): Promise<T> {
    let entry = reads.get(path);
    if (!entry) {
      const current = {
        valid: true,
        promise: undefined as unknown as Promise<unknown>,
      };
      current.promise = load(path)
        .then((data) => {
          if (!current.valid) return read<T>(path);
          snapshots.delete(path);
          snapshots.set(path, { at: Date.now(), data });
          if (snapshots.size > 40)
            snapshots.delete(snapshots.keys().next().value!);
          return data;
        })
        .finally(() => {
          if (reads.get(path) === current) reads.delete(path);
        });
      entry = current;
      reads.set(path, current);
    }
    return structuredClone(await entry.promise) as T;
  }
  return {
    snapshot,
    read,
    invalidate(matches: (path: string) => boolean = () => true) {
      for (const key of snapshots.keys())
        if (matches(key)) snapshots.delete(key);
      for (const [key, value] of reads)
        if (matches(key)) {
          value.valid = false;
          reads.delete(key);
        }
    },
  };
}
